/**
 * Overlay Popup - Newsletter
 * Custom elements for popup display with cookie-based dismissal,
 * configurable triggers, and device targeting.
 */

class PopupCookie {
  constructor(name) {
    this.name = name;
  }

  read() {
    const match = document.cookie.match(new RegExp('(^| )' + this.name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  write(value, days = 7) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${this.name}=${value}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
  }

  destroy() {
    document.cookie = `${this.name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
  }
}

class PopupComponent extends HTMLElement {
  constructor() {
    super();
    this.cookie = null;
    this.dialog = null;
    this.backdrop = null;
    this._scrollHandler = null;
    this._idleTimer = null;
    this._idleEvents = ['mousemove', 'keydown', 'scroll', 'touchstart'];
  }

  connectedCallback() {
    const cookieName = this.dataset.popupCookie || 'overlay-popup-dismissed';
    this.cookie = new PopupCookie(cookieName);

    this.dialog = this.querySelector('.overlay-popup-dialog');
    this.backdrop = this.querySelector('.overlay-popup-backdrop');

    if (!this.dialog) return;

    // If already dismissed via cookie, skip (unless in theme editor)
    if (this.cookie.read() && !Shopify.designMode) return;

    this._bindEvents();
    this._scheduleTrigger();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  _bindEvents() {
    // Close button
    const closeBtn = this.querySelector('.overlay-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Backdrop click closes large popup
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Escape key
    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.dialog.hasAttribute('open')) {
        this.close();
      }
    };
    document.addEventListener('keydown', this._escHandler);

    // Theme editor events
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.contains(this)) this.open(true);
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.contains(this)) this.close(true);
      });
    }
  }

  _scheduleTrigger() {
    const trigger = this.dataset.popupDelay || 'delayed_3';

    switch (trigger) {
      case 'always':
        this.open();
        break;

      case 'delayed_3':
        this._delayTimer = setTimeout(() => this.open(), 3000);
        break;

      case 'delayed_10':
        this._delayTimer = setTimeout(() => this.open(), 10000);
        break;

      case 'scroll_bottom':
        this._scrollHandler = () => {
          const scrollPercent = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
          if (scrollPercent >= 0.7) {
            this.open();
            window.removeEventListener('scroll', this._scrollHandler);
            this._scrollHandler = null;
          }
        };
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
        break;

      case 'idle_60':
        this._startIdleTimer();
        break;
    }
  }

  _startIdleTimer() {
    const resetIdle = () => {
      clearTimeout(this._idleTimer);
      this._idleTimer = setTimeout(() => {
        this.open();
        this._removeIdleListeners();
      }, 60000);
    };

    this._idleResetHandler = resetIdle;
    this._idleEvents.forEach((evt) => {
      document.addEventListener(evt, this._idleResetHandler, { passive: true });
    });

    // Start the initial timer
    resetIdle();
  }

  _removeIdleListeners() {
    if (this._idleResetHandler) {
      this._idleEvents.forEach((evt) => {
        document.removeEventListener(evt, this._idleResetHandler);
      });
      this._idleResetHandler = null;
    }
    clearTimeout(this._idleTimer);
  }

  _cleanup() {
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
    }
    if (this._delayTimer) {
      clearTimeout(this._delayTimer);
    }
    this._removeIdleListeners();
  }

  open(editorBypass = false) {
    if (!this.dialog) return;

    // Prevent opening if another modal is already open (e.g. cart drawer)
    if (!editorBypass && document.body.classList.contains('overflow-hidden')) return;

    this.dialog.setAttribute('open', '');
    if (this.backdrop) {
      this.backdrop.setAttribute('open', '');
    }

    document.body.classList.add('overflow-hidden');

    // Trap focus using Dawn's global function
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, this.dialog.querySelector('.overlay-popup__close'));
    }
  }

  close(editorBypass = false) {
    if (!this.dialog) return;

    this.dialog.removeAttribute('open');
    if (this.backdrop) {
      this.backdrop.removeAttribute('open');
    }

    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    // Write dismissal cookie (skip in editor)
    if (!editorBypass) {
      this.cookie.write('dismissed', 7);
    }
  }
}

class PopupNewsletter extends PopupComponent {
  connectedCallback() {
    // Apply device targeting CSS class
    const targetDevice = this.dataset.targetDevice || 'all';
    if (targetDevice === 'mobile') {
      this.classList.add('mobile-only');
    } else if (targetDevice === 'desktop') {
      this.classList.add('desktop-only');
    }

    // Runtime device check before proceeding
    if (targetDevice === 'mobile' && window.matchMedia('(min-width: 750px)').matches) {
      // Desktop visitor but popup targets mobile only; still register for resize
      this._watchDeviceChange(targetDevice);
    } else if (targetDevice === 'desktop' && window.matchMedia('(max-width: 749px)').matches) {
      this._watchDeviceChange(targetDevice);
    }

    super.connectedCallback();
    this._bindFormSubmit();

    // After native form submission + page reload, Liquid renders the result
    // into the DOM. Detect server-rendered success and force the popup open.
    const serverSuccess = this.querySelector('.overlay-popup__thank-you[id]');
    if (serverSuccess && this.dialog) {
      // If cookie was set, super.connectedCallback() skipped _bindEvents;
      // bind now so close button / escape key work
      if (this.cookie && this.cookie.read()) {
        this._bindEvents();
      }
      this.open();
      this.cookie.write('dismissed', 30);
    }
  }

  _bindFormSubmit() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('.overlay-popup__submit');
    this.loadingSpinner = this.submitButton
      ? this.submitButton.querySelector('.loading__spinner')
      : null;
    this.formContent = this.form.querySelector('.newsletter-form__field-wrapper');
    this.errorMsg = this.form.querySelector('.overlay-popup__error-msg');
    this.errorText = this.form.querySelector('.overlay-popup__error-text');

    // Client-side thank-you container (the one with .hidden class)
    const thankYouEls = this.form.querySelectorAll('.overlay-popup__thank-you');
    this.thankYouContainer = Array.from(thankYouEls).find((el) =>
      el.classList.contains('hidden')
    );

    this.form.addEventListener('submit', (e) => this._handleSubmit(e));

    // "Continue Shopping" buttons close the popup
    this.querySelectorAll('.overlay-popup__continue').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
  }

  _handleSubmit(e) {
    // Prevent double-submit
    if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      return;
    }
    // Show loading state; form submits natively so Shopify can handle
    // bot protection (CAPTCHA) challenges via redirect
    this._setLoading(true);
  }

  _setLoading(isLoading) {
    if (!this.submitButton) return;
    if (isLoading) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('loading');
      if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');
    }
  }

  _showSuccess() {
    if (this.formContent) this.formContent.classList.add('hidden');
    if (this.submitButton) this.submitButton.classList.add('hidden');
    if (this.thankYouContainer) {
      this.thankYouContainer.classList.remove('hidden');
      this.thankYouContainer.focus();
    }
    // Write cookie with 30-day expiry
    this.cookie.write('dismissed', 30);
  }

  _showError(message) {
    if (this.errorText) this.errorText.textContent = message;
    if (this.errorMsg) this.errorMsg.classList.remove('hidden');
  }

  _hideError() {
    if (this.errorMsg) this.errorMsg.classList.add('hidden');
  }

  _watchDeviceChange(targetDevice) {
    const mq = window.matchMedia(targetDevice === 'mobile' ? '(max-width: 749px)' : '(min-width: 750px)');
    mq.addEventListener('change', (e) => {
      if (e.matches && !this.dialog.hasAttribute('open') && !this.cookie.read()) {
        this._scheduleTrigger();
      }
    });
  }
}

if (!customElements.get('popup-component')) {
  customElements.define('popup-component', PopupComponent);
}
if (!customElements.get('popup-newsletter')) {
  customElements.define('popup-newsletter', PopupNewsletter);
}
